/*
 * adapted from
 * https://github.com/backstage/backstage/blob/master/plugins/catalog/src/components/CatalogPage/DefaultCatalogPage.tsx
 */

import {
  Content,
  CreateButton,
  PageWithHeader,
  SupportButton,
  TableColumn,
  TableProps,
} from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  CatalogFilterLayout,
  DefaultFilters,
  EntityListPagination,
  EntityListProvider,
  EntityOwnerPickerProps,
  UserListFilterKind,
} from '@backstage/plugin-catalog-react';
import React, { ReactNode } from 'react';
import { CatalogTable, CatalogTableRow, CatalogTableColumnsFunc  } from '@backstage/plugin-catalog';
import { catalogTranslationRef } from '@backstage/plugin-catalog/alpha';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { usePermission } from '@backstage/plugin-permission-react';
import { rootRouteRef } from '@backstage/plugin-scaffolder';


/** @internal */
export type CustomBaseCatalogPageProps = {
  filters: ReactNode;
  content?: ReactNode;
  pagination?: EntityListPagination;
};

function HeaderProxy({children}: {children: ReactNode}) {
  const style = {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    textAlign: 'left',
    alignItems: 'center',
    marginBottom: '16px',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  } as const
  return (
    <div style={style}>
      {children}
    </div>
  )
}

/** @internal */
export function CustomBaseCatalogPage(props: CustomBaseCatalogPageProps) {
  const { filters, content = <CatalogTable />, pagination } = props;
  const createComponentLink = useRouteRef(rootRouteRef);
  const { t } = useTranslationRef(catalogTranslationRef);
  const { allowed } = usePermission({
    permission: catalogEntityCreatePermission,
  });

  return (
    <PageWithHeader title="CAISE service catalog" themeId="home">
      <Content>
        <HeaderProxy>
          {allowed && (
            <CreateButton
              title={t('indexPage.createButtonTitle')}
              to={createComponentLink && createComponentLink()}
            />
          )}
          <SupportButton>{t('indexPage.supportButtonContent')}</SupportButton>
        </HeaderProxy>
        <EntityListProvider pagination={pagination}>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>{filters}</CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>{content}</CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Content>
    </PageWithHeader>
  );
}

/**
 * Props for root catalog pages.
 *
 * @public
 */
export interface CustomCatalogPageProps {
  initiallySelectedFilter?: UserListFilterKind;
  columns?: TableColumn<CatalogTableRow>[] | CatalogTableColumnsFunc;
  actions?: TableProps<CatalogTableRow>['actions'];
  initialKind?: string;
  tableOptions?: TableProps<CatalogTableRow>['options'];
  emptyContent?: ReactNode;
  ownerPickerMode?: EntityOwnerPickerProps['mode'];
  filters?: ReactNode;
  initiallySelectedNamespaces?: string[];
  pagination?: EntityListPagination;
}

export function CustomCatalogPage(props: CustomCatalogPageProps) {
  const {
    columns,
    actions,
    initiallySelectedFilter = 'owned',
    initialKind = 'component',
    tableOptions = {},
    emptyContent,
    pagination,
    ownerPickerMode,
    filters,
    initiallySelectedNamespaces,
  } = props;

  return (
    <CustomBaseCatalogPage
      filters={
        filters ?? (
          <DefaultFilters
            initialKind={initialKind}
            initiallySelectedFilter={initiallySelectedFilter}
            ownerPickerMode={ownerPickerMode}
            initiallySelectedNamespaces={initiallySelectedNamespaces}
          />
        )
      }
      content={
        <CatalogTable
          columns={columns}
          actions={actions}
          tableOptions={tableOptions}
          emptyContent={emptyContent}
        />
      }
      pagination={pagination}
    />
  );
}
